import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  syncCopyInfoFromStorage,
  readCopyInfoFromStorage,
  clearCopyInfo,
  selectCopyInfo,
} from '../redux/slicers/appSlicer';
import { COPY_INFO } from '../constants/projectConstants';
import { useUserContext } from '../contexts/UserContext';

/**
 * Manages the lifetime of copyInfo (which controls the "Paste & Apply" button):
 *
 * 1. Cross-tab sync: the `storage` event fires only in tabs other than the one
 *    that made the change, so every other open tab updates live.
 * 2. Auto-expiry: copied edits are dropped once their `expiresAt` passes, even
 *    if the page just sits idle. The timer is rescheduled whenever copyInfo
 *    changes (a new copy refreshes `expiresAt`, restarting the window).
 * 3. Org change: copied edits belong to one organization, so they are cleared
 *    whenever the current org id changes — regardless of how the switch
 *    happened (switch action, re-login, profile refresh, cross-tab sync).
 */
const useCopyInfoSync = () => {
  const dispatch = useDispatch();
  const copyInfo = useSelector(selectCopyInfo);
  const { currentUser } = useUserContext();
  const orgId = currentUser?.organization;
  const prevOrgId = useRef(orgId);

  // Cross-tab sync.
  useEffect(() => {
    const handleCopyInfoStorage = (event) => {
      if (event.key !== COPY_INFO) return;
      dispatch(syncCopyInfoFromStorage(readCopyInfoFromStorage()));
    };
    window.addEventListener('storage', handleCopyInfoStorage);
    return () => window.removeEventListener('storage', handleCopyInfoStorage);
  }, [dispatch]);

  // Auto-expiry. Re-runs whenever the copy or its expiry changes.
  useEffect(() => {
    if (!copyInfo?.hasCopiedEdits || !copyInfo?.expiresAt) return undefined;
    const remaining = copyInfo.expiresAt - Date.now();
    if (remaining <= 0) {
      dispatch(clearCopyInfo());
      return undefined;
    }
    const timer = setTimeout(() => dispatch(clearCopyInfo()), remaining);
    return () => clearTimeout(timer);
  }, [dispatch, copyInfo?.hasCopiedEdits, copyInfo?.expiresAt]);

  // Clear copied edits whenever the organization changes. The ref guard skips
  // the initial mount (null -> first org) so we only react to real switches.
  useEffect(() => {
    if (prevOrgId.current != null && prevOrgId.current !== orgId) {
      dispatch(clearCopyInfo());
    }
    prevOrgId.current = orgId;
  }, [dispatch, orgId]);
};

export default useCopyInfoSync;
